import { useState } from "react";
import { Check, Minus, Plus, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const ShopSection = ({ product, onAddToCart }) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedFlavor, setSelectedFlavor] = useState(product?.flavors?.[0] || "Sour Gummi Worm");

  const handleQuantityChange = (delta) => {
    setQuantity(prev => Math.max(1, Math.min(10, prev + delta)));
  };

  const handleAddToCart = (isSubscription = false) => {
    onAddToCart(quantity, isSubscription);
  };

  const subscriptionPrice = product ? (product.price * (1 - product.subscription_discount / 100)).toFixed(2) : "33.92";

  return (
    <section 
      className="py-20 md:py-28"
      data-testid="shop-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Product Image */}
          <div className="relative flex justify-center">
            {/* Glow Effect */}
            <div className="absolute w-[300px] h-[300px] lg:w-[400px] lg:h-[400px] bg-nukleus-violet/20 rounded-full blur-[80px]" />
            
            <img
              src="https://customer-assets.emergentagent.com/job_focus-powder/artifacts/5l4mwejz_Hero%20shot.jpg"
              alt="NUKLEUS Focus Powder"
              className="relative z-10 w-[280px] sm:w-[350px] lg:w-[450px] h-auto object-contain drop-shadow-2xl"
              data-testid="shop-product-image"
            />
          </div>

          {/* Product Details */}
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-nukleus-lilac mb-3 block">
              Focus Powder
            </span>
            
            <h2 className="text-3xl md:text-4xl font-semibold text-white tracking-tight mb-2">
              {product?.name || "NUKLEUS Focus Powder"}
            </h2>
            
            <p className="text-white/50 mb-6">
              {product?.tagline || "Sour Gummi Worm"} · {product?.servings || 30} servings
            </p>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-6">
              <span className="text-3xl font-bold text-white">
                ${product?.price || "39.90"}
              </span>
              {product?.compare_at_price && (
                <span className="text-lg text-white/30 line-through">
                  ${product.compare_at_price}
                </span>
              )}
              <span className="text-sm text-white/50">
                (${product?.per_serving_cost || "1.33"}/serving)
              </span>
            </div>

            {/* Features */}
            <div className="space-y-3 mb-8">
              {(product?.features || []).map((feature, index) => (
                <div key={index} className="flex items-center gap-3 text-white/70">
                  <div className="w-5 h-5 rounded-full bg-nukleus-violet/20 flex items-center justify-center">
                    <Check className="w-3 h-3 text-nukleus-violet" />
                  </div>
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>

            {/* Flavor Selector */}
            <div className="mb-6">
              <label className="text-sm font-medium text-white/70 mb-3 block">Flavor</label>
              <div className="flex flex-wrap gap-2">
                {(product?.flavors || ["Sour Gummi Worm"]).map((flavor) => (
                  <button
                    key={flavor}
                    onClick={() => setSelectedFlavor(flavor)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedFlavor === flavor
                        ? "bg-nukleus-violet text-white"
                        : "bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"
                    }`}
                    data-testid={`flavor-${flavor.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {flavor}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity Selector */}
            <div className="mb-8">
              <label className="text-sm font-medium text-white/70 mb-3 block">Quantity</label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 1}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="qty-decrease"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-12 text-center text-lg font-semibold text-white" data-testid="qty-value">
                  {quantity}
                </span>
                <button
                  onClick={() => handleQuantityChange(1)}
                  disabled={quantity >= 10}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="qty-increase"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={() => handleAddToCart(false)}
                className="w-full bg-nukleus-violet hover:bg-nukleus-violet/90 text-white rounded-full py-6 text-base font-semibold shadow-[0_0_30px_rgba(124,58,237,0.4)] hover:shadow-[0_0_40px_rgba(124,58,237,0.6)] transition-all btn-primary"
                data-testid="add-to-cart-btn"
              >
                Add to Cart — ${(product?.price * quantity || 44.99 * quantity).toFixed(2)}
              </Button>
              
              <Button
                onClick={() => handleAddToCart(true)}
                variant="outline"
                className="w-full bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-full py-6 text-base font-medium transition-all"
                data-testid="subscribe-btn"
              >
                Subscribe & Save {product?.subscription_discount || 15}% — ${(parseFloat(subscriptionPrice) * quantity).toFixed(2)}
              </Button>
            </div>

            {/* Guarantee */}
            <div className="flex items-center gap-3 mt-6 text-white/50">
              <ShieldCheck className="w-5 h-5 text-nukleus-violet" />
              <span className="text-sm">30-day money-back guarantee. No questions asked.</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShopSection;
